"use client";

import { Fragment, type ReactNode, useMemo } from "react";
import {
	Breadcrumb,
	BreadcrumbItem,
	BreadcrumbLink,
	BreadcrumbList,
	BreadcrumbPage,
	BreadcrumbSeparator,
} from "../components/breadcrumb";
import { Separator } from "../components/separator";
import { SidebarTrigger } from "../components/sidebar";
import type { NavLinkComponent } from "./types";

export interface SiteHeaderProps {
	pathname: string;
	LinkComponent: NavLinkComponent;
	/** Maps a path segment to a friendly breadcrumb label. */
	routeLabels?: Record<string, string>;
	homeLabel?: string;
	homeHref?: string;
	/**
	 * Prefix stripped from `pathname` before building breadcrumb segments (and
	 * re-prepended to segment links). Lets a nested root like `/dashboard`
	 * act as the breadcrumb home without duplicating the segment.
	 */
	basePath?: string;
	/** Right-aligned controls. */
	actions?: ReactNode;
}

function labelFor(
	segment: string,
	routeLabels: Record<string, string>,
): string {
	if (routeLabels[segment]) return routeLabels[segment];
	// Shorten UUIDs / long ids to something readable.
	if (/^[0-9a-f-]{8,}$/i.test(segment)) return `${segment.slice(0, 8)}…`;
	return segment;
}

export function SiteHeader({
	pathname,
	LinkComponent,
	routeLabels = {},
	homeLabel = "Dashboard",
	homeHref,
	basePath = "",
	actions,
}: SiteHeaderProps) {
	const Link = LinkComponent;
	const resolvedHomeHref = homeHref ?? basePath ?? "/";
	const segments = useMemo(() => {
		const relative =
			basePath && pathname.startsWith(basePath)
				? pathname.slice(basePath.length)
				: pathname;
		const parts = relative.split("/").filter(Boolean);
		return parts.map((segment, i) => ({
			segment,
			href: `${basePath}/${parts.slice(0, i + 1).join("/")}`,
			isLast: i === parts.length - 1,
		}));
	}, [pathname, basePath]);

	return (
		<header className="sticky top-0 z-10 flex h-(--header-height) shrink-0 items-center gap-2 border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
			<div className="flex w-full items-center gap-1 px-4 lg:gap-2 lg:px-6">
				<SidebarTrigger className="-ml-1" />
				<Separator orientation="vertical" className="mr-2 h-4 self-center!" />
				<Breadcrumb>
					<BreadcrumbList>
						<BreadcrumbItem>
							{segments.length === 0 ? (
								<BreadcrumbPage>{homeLabel}</BreadcrumbPage>
							) : (
								<BreadcrumbLink asChild>
									<Link href={resolvedHomeHref}>{homeLabel}</Link>
								</BreadcrumbLink>
							)}
						</BreadcrumbItem>
						{segments.map((s) => (
							<Fragment key={s.href}>
								<BreadcrumbSeparator />
								<BreadcrumbItem>
									{s.isLast ? (
										<BreadcrumbPage>
											{labelFor(s.segment, routeLabels)}
										</BreadcrumbPage>
									) : (
										<BreadcrumbLink asChild>
											<Link href={s.href}>
												{labelFor(s.segment, routeLabels)}
											</Link>
										</BreadcrumbLink>
									)}
								</BreadcrumbItem>
							</Fragment>
						))}
					</BreadcrumbList>
				</Breadcrumb>
				{actions ? (
					<div className="ml-auto flex items-center gap-2">{actions}</div>
				) : null}
			</div>
		</header>
	);
}
